import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  chartStatusConstants,
  dateConstants,
  filterConstants,
  qcStatusConstants,
  milestoneConstants,
  userRoleConstants,
  priorityConstants,
} from "../../utils/constants";
import { addPriorities } from "../../services/chartService";

const TabButton = ({ tabName, activeTab, onClick }) => (
  <button
    className={`btn btn-primary me-4 mt-4${
      activeTab === tabName ? " active" : ""
    }`}
    onClick={() => onClick(tabName)}
  >
    {tabName}
  </button>
);

const SubTabButton = ({ subTabName, activeSubTab, onClick }) => (
  <button
    className={`btn me-2 ${
      activeSubTab === subTabName ? "btn-primary" : "btn-secondary"
    }`}
    onClick={() => onClick(subTabName)}
  >
    {subTabName}
  </button>
);

const Card = ({ title, children }) => (
  <div className="card card-flush mt-4">
    <div className="card-header">
      <div className="card-title">
        <h4>{title}</h4>
      </div>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const PrioritizationConfiguration = ({ priorityData, setSuccess, success }) => {
  const [activeTab, setActiveTab] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("");
  const [roleId, setRoleId] = useState();
  const [priorityId, setPriorityId] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const conditions = [
    { label: "Milestones", value: filterConstants.Milestone },
    { label: "Qc Status", value: filterConstants.QCStatus },
    { label: "Chart Status", value: filterConstants.ChartStatus },
    { label: "Date of Service", value: filterConstants.DateOfService },
    { label: "Received date", value: filterConstants.ReceivedDate },
  ];

  const chartOptions = [
    { label: "Open", value: chartStatusConstants.OPEN_STATUS_ID },
    { label: "Complete", value: chartStatusConstants.COMPLETE_STATUS_ID },
    {
      label: "Incomplete",
      value: chartStatusConstants.INCOMPLETE_STATUS_ID,
    },
  ];
  const qcOptions = [
    { label: "Agree", value: qcStatusConstants.QC_PASS_ID },
    { label: "Feedback Provided", value: qcStatusConstants.QC_FAIL_ID },
    {
      label: "Feedback Implemented",
      value: qcStatusConstants.QC_IMPLEMENTED_ID,
    },
    { label: "Feedback Rejected", value: qcStatusConstants.QC_REJECTED_ID },
    { label: "Blank", value: 0 },
  ];
  const milestoneOptions = [
    {
      label: "Ready to Allocate",
      value: milestoneConstants.READY_TO_ALLOCATE_MILESTONE_ID,
    },
    {
      label: "Ready to code",
      value: milestoneConstants.READY_TO_CODE_MILESTONE_ID,
    },
    {
      label: "Ready to Audit",
      value: milestoneConstants.READY_TO_AUDIT_MILESTONE_ID,
    },
    {
      label: "Audit in Progress",
      value: milestoneConstants.AUDIT_IN_PROGRESS_MILESTONE_ID,
    },
    {
      label: "Coding in Progress",
      value: milestoneConstants.CODING_IN_PROGRESS_MILESTONE_ID,
    },
    { label: "Audit Done", value: milestoneConstants.AUDIT_DONE_MILESTONE_ID },
    {
      label: "Coding done",
      value: milestoneConstants.CODING_DONE_MILESTONE_ID,
    },
  ];

  const dateOptions = [
    { label: "Today", value: dateConstants.TODAY },
    { label: "Not Today", value: dateConstants.NOT_TODAY },
    { label: "Last 7 Days", value: dateConstants.LAST_WEEK },
    { label: "Last  Month", value: dateConstants.LAST_MONTH },
  ];

  const [conditionRows, setConditionRows] = useState([]);

  const filterOptions = (index) => {
    let check = conditionRows[index]?.condition?.value;
    if (check === filterConstants.Milestone) {
      return milestoneOptions;
    }
    if (check === filterConstants.QCStatus) {
      return qcOptions;
    }
    if (check === filterConstants.ChartStatus) {
      return chartOptions;
    }
    if (
      check === filterConstants.DateOfService ||
      check === filterConstants.ReceivedDate
    ) {
      return dateOptions;
    } else {
      return [];
    }
  };

  const addNewConditionRow = () => {
    const newRow = {
      condition: null,
      filters: null,
    };

    setConditionRows((prevRows) => [...prevRows, newRow]);
  };

  const removeConditionRow = (index) => {
    setConditionRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows.splice(index, 1);
      return updatedRows;
    });
  };

  const getPriorityData = async (roleId, priorityId) => {
    const filteredData = priorityData?.filter(
      (data) => data?.roleId === roleId && data?.priorityId === priorityId,
    );

    if (filteredData?.length > 0 && filteredData[0]?.json) {
      return JSON.parse(filteredData[0].json);
    } else {
      return [];
    }
  };
  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const data = await getPriorityData(roleId, priorityId);
        const mappedDataWithCondition = mapValuesToLabelsWithCondition(data);
        setConditionRows(mappedDataWithCondition);
      } catch (error) {
        console.error("Error fetching priority data:", error);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
        }, 200);
      }
    };

    fetchData();
  }, [roleId, priorityId, priorityData]);

  const mapValuesToLabelsWithCondition = (data) => {
    return data.map((row) => {
      const conditionLabel = conditions.find(
        (opt) => opt.value === row.condition,
      );
      let filtersLabels;

      if (row.condition === filterConstants.Milestone) {
        filtersLabels = filterLabels(row?.filters, milestoneOptions);
      }
      if (row.condition === filterConstants.QCStatus) {
        filtersLabels = filterLabels(row?.filters, qcOptions);
      }
      if (row.condition === filterConstants.ChartStatus) {
        filtersLabels = filterLabels(row?.filters, chartOptions);
      }
      if (
        row.condition === filterConstants.ReceivedDate ||
        row.condition === filterConstants.DateOfService
      ) {
        filtersLabels = filterLabels(row?.filters, dateOptions);
      }

      return {
        condition: conditionLabel,
        filters: filtersLabels,
      };
    });
  };

  const filterLabels = (filters, options) => {
    return Array.isArray(filters)
      ? filters.map((filterValue) =>
          options.find((opt) => opt.value === filterValue),
        )
      : options.find((opt) => opt.value === filters);
  };

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    if (tabName === "TeamLead/Manager") {
      setRoleId(userRoleConstants.TEAM_LEAD_ID);
    }
    if (tabName === "Auditor") {
      setRoleId(userRoleConstants.AUDITOR_ID);
    }
    if (tabName === "Coder") {
      setRoleId(userRoleConstants.CODER_ID);
    }
    setActiveSubTab(null);
    setConditionRows([]);
  };

  const handleSubTabClick = (subTabName) => {
    setActiveSubTab(subTabName);
    if (subTabName === "High") {
      setPriorityId(priorityConstants.HIGH_PRIORITY_ID);
    }
    if (subTabName === "Medium") {
      setPriorityId(priorityConstants.MED_PRIORITY_ID);
    }
    if (subTabName === "Low") {
      setPriorityId(priorityConstants.LOW_PRIORITY_ID);
    }
    setConditionRows([]);
  };

  const handleBackClick = () => {
    setActiveTab(null);
    setActiveSubTab(null);
    setConditionRows([]);
  };

  const getFilteredConditions = () => {
    const selectedValues = conditionRows.map((row) => row.condition?.value);
    return conditions.filter(
      (condition) => !selectedValues.includes(condition.value),
    );
  };

  const saveConditions = async () => {
    try {
      setIsSaving(true);

      const payload = {
        roleId: roleId,
        priorityId: priorityId,
        json: conditionRows.map((row) => ({
          condition: row?.condition?.value,
          filters: Array.isArray(row?.filters)
            ? row.filters.map((filter) => filter?.value)
            : row.filters?.value,
        })),
      };

      if (conditionRows.some((row) => !row?.condition?.value)) {
        window.toastr.error("Conditions should not be empty or remove it");
      } else {
        const res = await addPriorities(payload);
        if (res?.ok) {
          window.toastr.success("Conditions are updated successfully");
        }
      }
    } catch (error) {
      console.error("Error saving conditions:", error);
      window.toastr.error("An error occurred while saving conditions");
    } finally {
      setIsSaving(false);
      setSuccess(!success);
    }
  };

  return (
    <div className="d-flex flex-column gap-7 gap-lg-5">
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12 col-12">
          {activeTab ? (
            <>
              <button
                className="btn btn-secondary float-end"
                onClick={handleBackClick}
              >
                x
              </button>
            </>
          ) : null}
          {activeTab ? (
            <>
              <h3 className="mb-3">{activeTab}</h3>
            </>
          ) : null}
          {activeTab ? null : (
            <>
              <TabButton
                tabName="TeamLead/Manager"
                activeTab={activeTab}
                onClick={handleTabClick}
              />
              <TabButton
                tabName="Auditor"
                activeTab={activeTab}
                onClick={handleTabClick}
              />
              <TabButton
                tabName="Coder"
                activeTab={activeTab}
                onClick={handleTabClick}
              />
            </>
          )}
          {activeTab && (
            <div className="d-flex">
              <SubTabButton
                subTabName="High"
                activeSubTab={activeSubTab}
                onClick={handleSubTabClick}
              />
              <SubTabButton
                subTabName="Medium"
                activeSubTab={activeSubTab}
                onClick={handleSubTabClick}
              />
              <SubTabButton
                subTabName="Low"
                activeSubTab={activeSubTab}
                onClick={handleSubTabClick}
              />
            </div>
          )}
          {isLoading && priorityData ? (
            <div className="d-flex justify-content-center mt-6 mb-6">
              <span className="spinner-border h-50px w-50px text-gray-400" />
            </div>
          ) : (
            <>
              {" "}
              {activeTab && activeSubTab && (
                <Card title={activeSubTab.toUpperCase()}>
                  <div>
                    <div className="row">
                      <div className="col-md-4 col-sm-12 col-12 mb-4">
                        <button
                          className="btn btn-primary mb-2"
                          onClick={addNewConditionRow}
                          disabled={
                            conditionRows?.length === 5 ||
                            conditionRows[conditionRows?.length - 1]
                              ?.condition === null
                          }
                        >
                          <i className="fa-solid fa-plus" /> Add Condition
                        </button>
                      </div>
                    </div>
                    {conditionRows?.length > 0 &&
                      conditionRows.map((condition, index) => (
                        <div className="row" key={index}>
                          <div className="col-md-3 col-sm-12 col-12 mb-4">
                            <label>Select a condition</label>
                            <Select
                              options={getFilteredConditions(index)}
                              value={condition.condition}
                              required
                              onChange={(selectedOption) =>
                                setConditionRows((prevRows) => {
                                  const updatedRows = [...prevRows];
                                  updatedRows[index].condition = selectedOption;
                                  return updatedRows;
                                })
                              }
                              isDisabled={conditionRows[index].filters !== null}
                            />
                          </div>
                          <div className=" col-md-6 col-sm-12 col-12 mb-4">
                            <label>Select a Status</label>
                            <Select
                              isMulti={
                                ![
                                  filterConstants.DateOfService,
                                  filterConstants.ReceivedDate,
                                ].includes(
                                  conditionRows[index]?.condition?.value,
                                )
                              }
                              required
                              options={filterOptions(index)}
                              value={condition.filters}
                              onChange={(selectedOptions) =>
                                setConditionRows((prevRows) => {
                                  const updatedRows = [...prevRows];
                                  updatedRows[index].filters = selectedOptions;
                                  return updatedRows;
                                })
                              }
                            />
                          </div>
                          <div className="col-md-3 col-sm-12 col-12">
                            <button
                              className="btn btn-danger mt-6 mb-4"
                              onClick={() => removeConditionRow(index)}
                            >
                              <i className="fa-solid fa-trash" /> Remove
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </Card>
              )}
            </>
          )}
          {activeTab && activeSubTab && (
            <div className="d-flex justify-content-end mt-6">
              <button
                type="button"
                className="btn btn-success me-4"
                disabled={isSaving}
                onClick={saveConditions}
              >
                <span className="indicator-label">
                  {isSaving ? "Please wait..." : "Save"}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrioritizationConfiguration;
